import { cleanChatText } from '@continuum/show-core';
import type { ChatSeen, ChatTo, ChatView, WireChat } from '@continuum/show-core';
import { NO_ANSWER, OFFLINE } from './chatText.js';
import { markRead, threadFromKey } from './threads.js';
import type { ReadMarks, SeenMarks, ThreadKey } from './threads.js';

/** How long a message waits for the room's word before the phone says it did not go. */
const ANSWER_MS = 10_000;
/** What this phone has read, by evening. Never a message: only where the guest got to. */
const READ_KEY = 'continuum.chat.read';

/** A message this phone sent, until the room has it. */
export interface Outgoing {
  /** The socket's message id; below zero for one that never left the phone. */
  id: number;
  to: ChatTo;
  text: string;
  at: number;
  /** Why it did not go (the room's refusal, `offline`, `no answer`); null while it is waiting. */
  error: string | null;
}

export interface ChatSnapshot {
  /** Null until the room has sent this phone its messages. */
  evening: string | null;
  messages: readonly WireChat[];
  saboteurs: readonly number[] | null;
  outgoing: readonly Outgoing[];
  read: ReadMarks;
  /** How far the others have read this phone's conversations, for Sent and Read. */
  seen: SeenMarks;
  /** The newest message that came in while the phone was listening, for the buzz and the notice. */
  arrived: WireChat | null;
}

type Transport = (to: ChatTo, text: string) => number | null;
/** Tells the room how far this guest has read a conversation; dropped while offline. */
type ReadTransport = (to: ChatTo, n: number) => void;

/**
 * One phone's messages. The room sends them to this phone alone (show-core `chat.ts`); the store
 * holds them beside the show state, for as long as the page is open, and keeps nothing of them on
 * the phone. Only how far the guest has read is remembered, for this evening only.
 */
export class ChatStore {
  private snap: ChatSnapshot = { evening: null, messages: [], saboteurs: null, outgoing: [], read: {}, seen: {}, arrived: null };
  private listeners = new Set<() => void>();
  private offline = 0;

  constructor(
    private readonly me: number,
    private readonly transport: Transport,
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeStorage(),
    private readonly tellRead: ReadTransport = () => undefined,
  ) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  snapshot = (): ChatSnapshot => this.snap;

  private set(next: ChatSnapshot): void {
    this.snap = next;
    for (const fn of this.listeners) fn();
  }

  /** Whatever the room said about messages: this phone's view, a new message, or an answer. */
  receive(msg: Record<string, unknown>): void {
    const s = this.snap;
    switch (msg.t) {
      case 'chatView': {
        const view = msg.view as ChatView;
        const same = view.evening === s.evening;
        // A message that did get through before the line dropped is in the view: stop waiting for it.
        const landed = (o: Outgoing) =>
          view.messages.some((m) => m.from === this.me && m.to === o.to && m.text === o.text && m.at >= o.at - 60_000);
        this.set({
          ...s,
          evening: view.evening,
          messages: view.messages,
          saboteurs: view.saboteurs,
          // A new evening: nothing of the last one is kept, not even what was waiting to go.
          outgoing: same ? s.outgoing.filter((o) => !landed(o)) : [],
          read: same ? s.read : this.readFor(view.evening),
          seen: seenFrom(view.seen ?? []),
          arrived: same ? s.arrived : null,
        });
        // The room may not have heard where this guest got to (read while the line was down): say it
        // again. The room keeps only marks that go further, so this is never more than a nudge.
        for (const [key, n] of Object.entries(this.snap.read)) {
          const to = threadFromKey(key);
          if (to !== null && n > 0) this.tellRead(to, n);
        }
        return;
      }
      case 'chatSeen': {
        const seen = msg.seen as ChatSeen;
        const key = String(seen.thread);
        const was = s.seen[key]?.[String(seen.seat)];
        if (was && was.n >= seen.n) return;
        this.set({ ...s, seen: { ...s.seen, [key]: { ...(s.seen[key] ?? {}), [String(seen.seat)]: { n: seen.n, at: seen.at } } } });
        return;
      }
      case 'chat': {
        const message = msg.message as WireChat;
        if (s.messages.some((m) => m.n === message.n)) return;
        const messages = [...s.messages, message].sort((a, b) => a.n - b.n);
        this.set({ ...s, messages, arrived: message.from === this.me ? s.arrived : message });
        return;
      }
      case 'ack':
        this.set({ ...s, outgoing: s.outgoing.filter((o) => o.id !== msg.id) });
        return;
      case 'nack':
        this.set({
          ...s,
          outgoing: s.outgoing.map((o) => (o.id === msg.id ? { ...o, error: String(msg.error) } : o)),
        });
        return;
      default:
        return;
    }
  }

  /** Send a message; it waits in `outgoing` until the room takes it or turns it down. */
  send(to: ChatTo, raw: string, now = Date.now()): void {
    // Cleaned as the room will clean it, so the phone can match the room's copy to its own.
    const text = cleanChatText(raw);
    if (!text) return;
    const id = this.transport(to, text);
    const entry: Outgoing = { id: id ?? --this.offline, to, text, at: now, error: id === null ? OFFLINE : null };
    this.set({ ...this.snap, outgoing: [...this.snap.outgoing, entry] });
    if (id === null) return;
    setTimeout(() => {
      const waiting = this.snap.outgoing.find((o) => o.id === id && o.error === null);
      if (!waiting) return;
      this.set({ ...this.snap, outgoing: this.snap.outgoing.map((o) => (o === waiting ? { ...o, error: NO_ANSWER } : o)) });
    }, ANSWER_MS);
  }

  /** A message that did not go, taken back (the guest is sending it again, or not). */
  discard(id: number): void {
    this.set({ ...this.snap, outgoing: this.snap.outgoing.filter((o) => o.id !== id) });
  }

  /** The guest has seen this conversation to its end. */
  read(key: ThreadKey): void {
    const read = markRead(this.snap.read, this.snap.messages, this.me, key);
    if (read === this.snap.read) return;
    this.set({ ...this.snap, read });
    this.tellRead(key, read[String(key)] ?? 0);
    if (!this.snap.evening) return;
    try {
      this.storage?.setItem(READ_KEY, JSON.stringify({ evening: this.snap.evening, read }));
    } catch {
      /* private browsing: the badge simply counts from the start again after a reload */
    }
  }

  /** Where the guest had read to, if it was this evening; one key, replaced every evening. */
  private readFor(evening: string): ReadMarks {
    try {
      const kept = JSON.parse(this.storage?.getItem(READ_KEY) ?? 'null') as { evening?: string; read?: ReadMarks } | null;
      return kept?.evening === evening && kept.read ? kept.read : {};
    } catch {
      return {};
    }
  }
}

function seenFrom(list: readonly ChatSeen[]): SeenMarks {
  const seen: SeenMarks = {};
  for (const x of list) seen[String(x.thread)] = { ...(seen[String(x.thread)] ?? {}), [String(x.seat)]: { n: x.n, at: x.at } };
  return seen;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
